//! This is injected globally, make sure to actually include things that a majority of components will use
//! If you need to make a more specific set of utilities you should make a new mixin and include it where it is needed

import {
  ButtonSize,
  ButtonVariant,
} from '@/components/VfButton/VfButton.types';
import { formatDateForDisplay } from '@/helpers/formatDateForDisplay';
import { NestedKeyOf, Role } from 'types';
import { ComponentOptions } from 'vue';
import localization from '@/localization/root';

export type ToastMixin = (
  titleText: string,
  content: string,
  type: 'success' | 'warning' | 'danger',
  delay?: number,
  autoHide?: boolean,
) => void;

type TranslationKey = NestedKeyOf<(typeof localization)['de']>;

export type IsUserRoleMixin = (roles: Role[]) => boolean;

export type FormatDateForDisplayMixin = (date: Date) => string;

export type PredefinedCreateToastMethod = (
  content?: string,
  delay?: number,
) => void;

export type $tt = (
  key: TranslationKey,
  values?: Record<string, string>,
) => string;

export type GlobalData = {
  buttonSizes: {
    small: ButtonSize.SMALL;
    medium: ButtonSize.MEDIUM;
    large: ButtonSize.LARGE;
  };
  buttonVariants: {
    primary: ButtonVariant.PRIMARY;
    secondary: ButtonVariant.SECONDARY;
    tertiary: ButtonVariant.TERTIARY;
    quaternary: ButtonVariant.QUATERNARY;
  };
};

export type GlobalUtils = {
  createErrorToast: PredefinedCreateToastMethod;
  createSuccessToast: PredefinedCreateToastMethod;
  createWarningToast: PredefinedCreateToastMethod;
  createToast: ToastMixin;
  isUserRole: IsUserRoleMixin;
  formatDateForDisplay: FormatDateForDisplayMixin;
  $tt: $tt;
} & GlobalData;

const globalUtils: ComponentOptions = {
  data(): GlobalData {
    return {
      buttonSizes: {
        small: ButtonSize.SMALL,
        medium: ButtonSize.MEDIUM,
        large: ButtonSize.LARGE,
      },
      buttonVariants: {
        primary: ButtonVariant.PRIMARY,
        secondary: ButtonVariant.SECONDARY,
        tertiary: ButtonVariant.TERTIARY,
        quaternary: ButtonVariant.QUATERNARY,
      },
    };
  },
  methods: {
    createToast(
      titleText: string,
      content: string,
      type: 'success' | 'warning' | 'danger',
      delay = 5000,
      autoHide = true,
    ) {
      const severity = this.getSeverity(type);
      this.$toast.add({
        severity,
        summary: titleText,
        detail: content,
        life: autoHide ? delay : undefined,
      });
    },

    createSuccessToast(content?: string, delay?: number) {
      this.createToast(
        this.$t('generic.success'),
        content ?? '',
        'success',
        delay,
      );
    },

    createWarningToast(content?: string, delay?: number) {
      this.createToast(
        this.$t('generic.warning'),
        content ?? '',
        'warning',
        delay,
      );
    },

    createErrorToast(content?: string, delay?: number) {
      this.createToast(this.$t('errors.error'), content ?? '', 'danger', delay);
    },

    getSeverity(type: 'success' | 'warning' | 'danger') {
      switch (type) {
        case 'success':
          return 'success';
        case 'warning':
          return 'warn';
        case 'danger':
          return 'error';
      }
    },

    isUserRole(roles: Role[]): boolean {
      const isUserRole: boolean = this.$store.state.userRoles?.some(
        (role: Role) => roles.includes(role),
      );

      return isUserRole;
    },

    formatDateForDisplay(date: Date): string {
      return formatDateForDisplay(date);
    },

    $tt(key: TranslationKey, values?: Record<string, unknown>): string {
      return this.$t(key, values);
    },
  },
};

export default globalUtils;
